import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import type { QueryClient } from '@tanstack/react-query';
import { AuthContext } from './authContext';

/**
 * Wraps one target's Supabase client (business or admin — see
 * `lib/supabaseClient.business.ts` / `.admin.ts`) and tracks its auth
 * session for the whole app. Takes `queryClient` as a prop (not via
 * `useQueryClient()`) so this component doesn't need to render *inside*
 * `QueryClientProvider` — both entry points currently nest it the other way
 * around (`<AuthProvider><QueryClientProvider>…`), and threading the same
 * client instance in by prop avoids a context-order dependency either way.
 */
export function AuthProvider({
  supabase,
  queryClient,
  onSessionEnded,
  children,
}: {
  supabase: SupabaseClient;
  queryClient: QueryClient;
  /** Replaces the default `queryClient.clear()` on sign-out / account
   * switch. The business build passes the desk engine's handler (Task 14),
   * which clears the query cache at once but keeps UNSYNCED desk events
   * (paused mutations) until the receptionist syncs, downloads or discards
   * them — `clear()` would also drop the mutation cache, i.e. lose a
   * recorded check-in or balance silently. */
  onSessionEnded?: () => void;
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Tracks whose data the query cache currently holds, so a sign-out OR a
  // switch to a *different* user's session (without an explicit sign-out —
  // e.g. a fresh magic-link/invite overwriting an existing session) clears
  // it (fix round 1, item 2). A stale cache otherwise lets one account's
  // team/booking data render for the next account signed into the same tab.
  const currentUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    supabase.auth
      .getSession()
      .then(({ data: { session: initial } }) => {
        if (cancelled) return;
        currentUserIdRef.current = initial?.user.id ?? null;
        setSession(initial);
        setIsLoading(false);
      })
      .catch(() => {
        // getSession() itself failing (corrupt storage, a thrown parse
        // error) must not leave the app stuck on a loading screen forever
        // (fix round 1, item 9) — treat it as "not signed in" so the root
        // guards can send the visitor to sign-in rather than spinning.
        if (cancelled) return;
        currentUserIdRef.current = null;
        setSession(null);
        setIsLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // Ported deliberately from frontend/src/context/AuthContext.tsx (plan
      // L520-526), not RN-specific. supabase-js v2 fires this callback
      // while still holding GoTrue's internal auth lock. Setting state
      // synchronously here is fine on its own, but the whole point of this
      // callback is to unblock components (usePortalSession /
      // useAdminSession, both TanStack Query hooks keyed off `session`)
      // that immediately turn around and make their OWN Supabase call. If
      // that happens before the lock is released, it can deadlock —
      // exactly what hung profile-setup's save() on mobile after an
      // auth.updateUser-triggered USER_UPDATED event, on the same kind of
      // slow-LTE connection this portal targets. setTimeout(…, 0) defers
      // the state update (and everything downstream of it) until after
      // this callback returns and GoTrue releases the lock.
      setTimeout(() => {
        if (cancelled) return;

        const nextUserId = nextSession?.user.id ?? null;
        const previousUserId = currentUserIdRef.current;
        const switchedAccounts =
          previousUserId !== null && nextUserId !== null && nextUserId !== previousUserId;
        if (event === 'SIGNED_OUT' || switchedAccounts) {
          if (onSessionEnded) onSessionEnded();
          else queryClient.clear();
        }
        currentUserIdRef.current = nextUserId;

        setSession(nextSession);
        setIsLoading(false);
      }, 0);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [supabase, queryClient, onSessionEnded]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, [supabase]);

  const value = useMemo(
    () => ({ supabase, session, isLoading, signOut }),
    [supabase, session, isLoading, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
